import Tesseract from 'tesseract.js';
import { categoryService } from './categoryService';
import { getISODateInBarbados } from '../utils/timezone';

export const ocrService = {
  /**
   * Extracts transaction details from a receipt image using Tesseract.js OCR.
   * @param {string} imageUrl - URL or base64 data URL of the receipt image.
   * @returns {Promise<Object>} Extracted transaction details with confidence scores.
   */
  async extractReceiptData(imageUrl) {
    try {
      // Get all available categories for suggestion
      const allCategories = await categoryService?.getAll();
      const expenseCategories = allCategories?.filter(cat => cat?.type === 'expense');

      // Preprocess image for better OCR accuracy
      const preprocessedImage = await this.preprocessImage(imageUrl);

      // Perform OCR using Tesseract.js with optimized settings
      const result = await Tesseract?.recognize(
        preprocessedImage,
        'eng',
        {
          logger: (m) => {
            // Optional: log progress
            if (m?.status === 'recognizing text') {
              console.log(`OCR Progress: ${Math.round(m?.progress * 100)}%`);
            }
          },
          // Optimized Tesseract parameters for receipt scanning
          tessedit_pageseg_mode: Tesseract?.PSM?.AUTO,
          tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz .,/$-:',
        }
      );

      const text = result?.data?.text || '';
      const confidence = result?.data?.confidence || 0;

      console.log('OCR Raw Text:', text);
      console.log('OCR Confidence:', confidence);

      // Parse extracted text
      const parsedData = this.parseReceiptText(text, expenseCategories);

      return {
        merchantName: parsedData?.merchantName || '',
        merchantConfidence: parsedData?.merchantConfidence || 0,
        date: parsedData?.date || getISODateInBarbados(),
        dateConfidence: parsedData?.dateConfidence || 0,
        totalAmount: parsedData?.totalAmount || 0,
        amountConfidence: parsedData?.amountConfidence || 0,
        taxAmount: parsedData?.taxAmount || 0,
        taxConfidence: parsedData?.taxConfidence || 0,
        categoryId: parsedData?.categoryId || null,
        categoryName: parsedData?.categoryName || '',
        categoryConfidence: parsedData?.categoryConfidence || 0,
        lineItems: parsedData?.lineItems || [],
        qualityAssessment: `OCR completed with ${Math.round(confidence)}% confidence. ${parsedData?.totalAmount > 0 ? 'Amount detected.' : 'Please verify amount.'} ${parsedData?.merchantName ? 'Merchant detected.' : 'Please enter merchant name.'}`,
        overallConfidence: confidence / 100,
        rawText: text
      };
    } catch (error) {
      console.error('Error in OCR extraction:', error);
      throw new Error('Failed to process receipt image. Please ensure the image is clear and try again.');
    }
  },

  /**
   * Preprocesses image to improve OCR accuracy.
   * @param {string} imageUrl - Original image URL or base64.
   * @returns {Promise<string>} Preprocessed image as base64.
   */
  async preprocessImage(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size to image size
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw original image
        ctx.drawImage(img, 0, 0);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Apply grayscale and contrast enhancement
        for (let i = 0; i < data.length; i += 4) {
          // Convert to grayscale
          const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          
          // Apply contrast enhancement (simple threshold)
          const threshold = 128;
          const enhanced = gray > threshold ? 255 : 0;
          
          data[i] = enhanced;     // Red
          data[i + 1] = enhanced; // Green
          data[i + 2] = enhanced; // Blue
        }
        
        // Put processed image back
        ctx.putImageData(imageData, 0, 0);
        
        // Return as base64
        resolve(canvas.toDataURL('image/png'));
      };
      
      img.onerror = () => {
        // If preprocessing fails, return original image
        console.warn('Image preprocessing failed, using original image');
        resolve(imageUrl);
      };
      
      img.src = imageUrl;
    });
  },

  /**
   * Parses raw OCR text to extract structured receipt data.
   * @param {string} text - Raw text from OCR.
   * @param {Array} categories - Available expense categories.
   * @returns {Object} Parsed receipt data.
   */
  parseReceiptText(text, categories) {
    const lines = text?.split('\n')?.filter(line => line?.trim()?.length > 0);
    
    let merchantName = '';
    let merchantConfidence = 0;
    let date = '';
    let dateConfidence = 0;
    let totalAmount = 0;
    let amountConfidence = 0;
    let taxAmount = 0;
    let taxConfidence = 0;
    let lineItems = [];

    // Extract merchant name (usually first few non-empty lines)
    if (lines?.length > 0) {
      // Try first 3 lines for merchant name (skip very short lines)
      for (let i = 0; i < Math.min(3, lines?.length); i++) {
        const line = lines?.[i]?.trim();
        if (line?.length > 3 && !this.isDateLine(line) && !this.isAmountLine(line)) {
          merchantName = line;
          merchantConfidence = 0.7;
          break;
        }
      }
    }

    // Enhanced date patterns
    const datePatterns = [
      /\b(\d{1,2})[\.\/\-](\d{1,2})[\.\/\-](\d{2,4})\b/,  // MM/DD/YYYY or DD.MM.YYYY
      /\b(\d{4})[\.\/\-](\d{1,2})[\.\/\-](\d{1,2})\b/,  // YYYY-MM-DD
      /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,]+(\d{1,2})[\s,]+(\d{4})\b/i,  // Month DD, YYYY
      /\b(\d{1,2})[\s]+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,]+(\d{4})\b/i   // DD Month YYYY
    ];

    for (const line of lines) {
      for (const pattern of datePatterns) {
        let match = line?.match(pattern);
        if (match) {
          date = this.normalizeDate(match?.[0]);
          dateConfidence = 0.8;
          break;
        }
      }
      if (date) break;
    }

    // Enhanced amount extraction with better keyword matching
    const totalKeywords = ['total', 'amount due', 'balance due', 'grand total', 'amount', 'balance', 'due', 'pay'];
    const taxKeywords = ['tax', 'gst', 'hst', 'vat', 'sales tax', 'vat/tax'];
    const subtotalKeywords = ['subtotal', 'sub total', 'sub-total'];
    
    let allAmounts = [];
    
    for (let i = 0; i < lines?.length; i++) {
      const line = lines?.[i];
      const lineLower = line?.toLowerCase();
      
      // Look for total amount (highest priority)
      const hasTotal = totalKeywords?.some(keyword => lineLower?.includes(keyword));
      if (hasTotal) {
        const amounts = this.extractAmounts(line);
        if (amounts?.length > 0) {
          totalAmount = Math.max(...amounts);
          amountConfidence = 0.9;
        }
      }

      // Look for tax amount
      const hasTax = taxKeywords?.some(keyword => lineLower?.includes(keyword));
      if (hasTax) {
        const amounts = this.extractAmounts(line);
        if (amounts?.length > 0) {
          taxAmount = amounts?.[0];
          taxConfidence = 0.8;
        }
      }

      // Collect all amounts for fallback
      const amounts = this.extractAmounts(line);
      if (amounts?.length > 0) {
        allAmounts?.push(...amounts);
        
        // Extract line items (description + amount) - skip total/tax/subtotal lines
        const hasSubtotal = subtotalKeywords?.some(keyword => lineLower?.includes(keyword));
        if (!hasTotal && !hasTax && !hasSubtotal) {
          const description = line?.replace(/[\$\d\.\,]+/g, '')?.trim();
          if (description?.length > 2) {
            lineItems?.push({
              description: description,
              amount: amounts?.[0]
            });
          }
        }
      }
    }

    // If no total found, use the largest amount detected
    if (totalAmount === 0 && allAmounts?.length > 0) {
      totalAmount = Math.max(...allAmounts);
      amountConfidence = 0.5;
    }

    // Suggest category based on merchant name
    let categoryId = null;
    let categoryName = '';
    let categoryConfidence = 0;

    if (merchantName && categories?.length > 0) {
      const merchantLower = merchantName?.toLowerCase();
      
      // Enhanced keyword matching for common categories
      const categoryKeywords = {
        'groceries': ['grocery', 'supermarket', 'market', 'food', 'walmart', 'target', 'costco', 'safeway'],
        'dining': ['restaurant', 'cafe', 'coffee', 'diner', 'pizza', 'burger', 'starbucks', 'mcdonald', 'subway'],
        'gas': ['gas', 'fuel', 'petrol', 'shell', 'exxon', 'chevron', 'bp', 'mobil'],
        'pharmacy': ['pharmacy', 'drug', 'cvs', 'walgreens', 'rite aid'],
        'shopping': ['store', 'shop', 'retail', 'mall', 'amazon', 'ebay'],
        'entertainment': ['cinema', 'movie', 'theater', 'theatre', 'netflix', 'spotify'],
        'utilities': ['electric', 'water', 'gas bill', 'utility', 'power']
      };

      for (const [catType, keywords] of Object.entries(categoryKeywords)) {
        if (keywords?.some(keyword => merchantLower?.includes(keyword))) {
          const matchedCat = categories?.find(cat => 
            cat?.name?.toLowerCase()?.includes(catType) ||
            cat?.name?.toLowerCase()?.includes(catType?.slice(0, -1)) // singular form
          );
          if (matchedCat) {
            categoryId = matchedCat?.id;
            categoryName = matchedCat?.name;
            categoryConfidence = 0.7;
            break;
          }
        }
      }
    }

    return {
      merchantName,
      merchantConfidence,
      date,
      dateConfidence,
      totalAmount,
      amountConfidence,
      taxAmount,
      taxConfidence,
      categoryId,
      categoryName,
      categoryConfidence,
      lineItems: lineItems?.slice(0, 10) // Limit to 10 items
    };
  },

  /**
   * Checks if a line contains a date.
   */
  isDateLine(line) {
    return /\d{1,4}[\.\/\-]\d{1,2}[\.\/\-]\d{1,4}/?.test(line) || 
           /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i?.test(line);
  },

  /**
   * Checks if a line contains an amount.
   */
  isAmountLine(line) {
    return /\$?\s*\d+\.\d{2}/?.test(line);
  },

  /**
   * Extracts numeric amounts from a text line.
   * @param {string} text - Text line to parse.
   * @returns {Array<number>} Array of found amounts.
   */
  extractAmounts(text) {
    const amounts = [];
    // Enhanced pattern to match various amount formats: $12.34, 12.34, 1,234.56, 1234.56
    const amountPattern = /\$?\s*(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\b/g;
    let match;
    
    while ((match = amountPattern?.exec(text)) !== null) {
      const amount = parseFloat(match?.[1]?.replace(/,/g, ''));
      if (!isNaN(amount) && amount > 0 && amount < 1000000) { // Reasonable range
        amounts?.push(amount);
      }
    }
    
    return amounts;
  },

  /**
   * Normalizes various date formats to YYYY-MM-DD.
   * @param {string} dateStr - Date string to normalize.
   * @returns {string} Normalized date in YYYY-MM-DD format.
   */
  normalizeDate(dateStr) {
    try {
      let date = new Date(dateStr);
      if (!isNaN(date?.getTime())) {
        const year = date?.getFullYear();
        const month = String(date?.getMonth() + 1)?.padStart(2, '0');
        const day = String(date?.getDate())?.padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error('Date parsing error:', e);
    }
    return getISODateInBarbados();
  },

  /**
   * Validates receipt image quality before processing.
   * @param {File} file - Image file to validate.
   * @returns {Object} Validation result with suggestions.
   */
  validateReceiptImage(file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    if (!file) {
      return { valid: false, message: 'No file provided' };
    }

    if (file?.size > maxSize) {
      return { valid: false, message: 'Image size must be less than 10MB' };
    }

    if (!allowedTypes?.includes(file?.type)) {
      return { valid: false, message: 'Please upload a JPG, PNG, or WEBP image' };
    }

    return { valid: true, message: 'Image is valid for processing' };
  },

  /**
   * Converts File object to base64 data URL for API submission.
   * @param {File} file - Image file.
   * @returns {Promise<string>} Base64 data URL.
   */
  async fileToDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader?.result);
      reader.onerror = reject;
      reader?.readAsDataURL(file);
    });
  }
};